// ============================================================
// Content Studio · /square/projects/:id/studio
//
// Studio is the PROJECT-SCOPED creation surface. Everything it
// produces is scoped to one project and audited per run. Studio
// mounts the shared QuamSpaceCanvas in `studio` variant and adds:
//   - project framing copy (runs, agents, outputs)
//   - live MetricCards driven by useStudioRuns + useWorkspaceEvents
//   - the recent run history rail (sq_workspace_runs)
//   - the agent activity rail (sq_workspace_agent_events)
//   - the Build → Site Builder honest handoff seam (route navigation)
//
// Studio is intentionally distinct from QuamSpace and from Site Builder:
//   QuamSpace    = personal workspace / drafts / notes (scope='personal')
//   Studio       = per-project creation engine        (scope='project')
//   Site Builder = manual visual website construction  (sq_sites/*)
//
// Build mode in Studio renders the same generation lane as in
// QuamSpace, plus an "Open in Site Builder →" handoff that navigates
// to /square/projects/:id/site. The handoff is real and idempotent:
// it ensures the project owns a sq_sites row and, when the target
// page is still empty, can persist the current live run into real
// sq_site_blocks. Studio still generates; Site Builder still edits.
// ============================================================

import { useCallback, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { SHELL_TOKENS, text, timeAgo } from '../../shell/styles'
import EmptyState from '../../components/EmptyState'
import MetricCard from '../../components/MetricCard'
import ProjectSurface from './ProjectSurface'
import QuamSpaceCanvas from '../../components/QuamSpaceCanvas/QuamSpaceCanvas'
import { useStudioRuns } from '../../hooks/useStudioRuns'
import { useWorkspaceEvents } from '../../hooks/useWorkspaceEvents'
import { useLiveRun } from '../../hooks/useLiveRun'
import { useProject } from '../../hooks/useProjects'
import { api, isAuthError } from '../../api/client'
import { recordShellAction } from '../../observability/sentry'

function sectionText(section: {
  title?: string | null
  content_json?: Record<string, unknown>
}) {
  const content = section.content_json || {}
  return {
    title:
      section.title ||
      (typeof content.title === 'string' ? content.title : null) ||
      (typeof content.heading === 'string' ? content.heading : null) ||
      '',
    body:
      (typeof content.text === 'string' ? content.text : null) ||
      (typeof content.body === 'string' ? content.body : null) ||
      (typeof content.copy === 'string' ? content.copy : null) ||
      '',
  }
}

function mapSectionToBlock(
  section: {
    section_type?: string
    title?: string | null
    content_json?: Record<string, unknown>
  },
  index: number,
) {
  const text = sectionText(section)
  if (section.section_type === 'title' || index === 0) {
    return {
      blockType: 'hero',
      contentJson: {
        title: text.title || 'Untitled section',
        body: text.body,
      },
    }
  }

  if (section.section_type === 'callout') {
    return {
      blockType: 'feature',
      contentJson: {
        title: text.title || 'Callout',
        body: text.body,
      },
    }
  }

  return {
    blockType: 'text',
    contentJson: {
      title: text.title,
      body: text.body,
    },
  }
}

export default function ProjectStudio() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { project } = useProject(id)
  const { latestRun, runs, loading, error, refresh: refreshRuns } = useStudioRuns(id)
  const {
    events,
    loading: eventsLoading,
    error: eventsError,
  } = useWorkspaceEvents(id, 12)

  // Cache the resolved project workspace id across canvas Run clicks
  // so we don't pay an extra /api/workspace/list round-trip per run.
  // Reset is implicit on project change because the component
  // remounts under a different :id route param.
  const cachedWorkspaceIdRef = useRef<string | null>(null)

  // Live run binding for the canvas — drives real sections + events
  // overlay via realtime + /api/workspace/get hydration.
  const liveRun = useLiveRun()

  // Recover the most recent run on mount so a refresh lands the
  // operator back on the last work without re-pressing Run.
  useEffect(() => {
    if (!latestRun || !id) return
    if (liveRun.run?.id === latestRun.id) return
    liveRun.activate({
      workspaceId: latestRun.workspace_id,
      runId: latestRun.id,
    })
    // activate is stable; depend on latestRun id only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestRun?.id, id])

  const dispatchRun = useCallback(
    async (params: { prompt: string; mode: string }) => {
      if (!id) return null
      try {
        // Resolve or create the project's workspace.
        let workspaceId = cachedWorkspaceIdRef.current
        if (!workspaceId) {
          const list = await api.workspace.list({ scope: 'project', project_id: id, limit: 1 })
          workspaceId = list.workspaces[0]?.id ?? null
        }
        if (!workspaceId) {
          const created = await api.workspace.create({
            name: 'Project Studio',
            scope: 'project',
            projectId: id,
            mode: params.mode,
          })
          workspaceId = created.workspace.id
        }
        cachedWorkspaceIdRef.current = workspaceId

        // Real run dispatch — provider defaults are picked server-side
        // by determineProvider() in @square/core/workspace.
        const result = await api.workspace.run({
          workspaceId,
          mode: params.mode,
          prompt: params.prompt,
        })
        const runId = result?.run?.id ?? null
        recordShellAction('studio.run_dispatched', {
          projectId: id,
          mode: params.mode,
          workspaceId,
          runId,
        })
        // Bind the canvas to the live render path immediately so the
        // overlay shows real persisted sections as soon as the
        // realtime publication fires the first INSERT.
        if (runId) {
          liveRun.activate({ workspaceId, runId })
        }
        // Pull fresh runs/events after dispatch so the live rails update.
        await refreshRuns()
        return runId ? { workspaceId, runId } : null
      } catch (err) {
        if (isAuthError(err)) {
          recordShellAction('studio.run_blocked_auth_required', { projectId: id })
          return null
        }
        recordShellAction('studio.run_dispatch_failed', {
          projectId: id,
          message: (err as Error)?.message ?? 'unknown',
        })
        return null
      }
    },
    [id, refreshRuns, liveRun],
  )

  return (
    <ProjectSurface
      kicker="PROJECT · CONTENT STUDIO"
      title="Content Studio"
      description="Project-scoped creation engine — runs, agents, and outputs scoped to this project. Documents, PDF, slides, sheets, notes, and build."
    >
      {/* Live KPI strip — sourced from sq_workspace_runs / sq_workspace_agent_events */}
      <section
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: 10,
          marginBottom: 18,
        }}
      >
        <MetricCard
          label="Latest Run"
          value={latestRun?.status || '—'}
          sub={latestRun ? `${latestRun.mode} · ${latestRun.provider}` : 'no runs yet'}
          tone={
            latestRun?.status === 'error'
              ? 'critical'
              : latestRun?.status === 'running'
                ? 'warning'
                : 'default'
          }
        />
        <MetricCard
          label="Workspace Runs"
          value={runs.length}
          sub={runs.length ? 'project history loaded' : 'no run history'}
        />
        <MetricCard
          label="Agent Activity"
          value={events.length}
          sub={events.length ? 'recent events loaded' : 'quiet'}
        />
      </section>

      {/* Shared creation canvas — studio variant */}
      <div style={{ marginBottom: 26 }}>
        <QuamSpaceCanvas
          variant="studio"
          projectId={id}
          initialMode="document"
          live={{
            runsCount: runs.length,
            agentEventsCount: events.length,
            loading,
            error,
            latestRunMode: latestRun?.mode || null,
            latestRunStatus: latestRun?.status || null,
          }}
          onRequestRun={(params) => dispatchRun(params)}
          liveRun={{
            runId: liveRun.run?.id ?? null,
            workspaceId: liveRun.run?.workspace_id ?? null,
            status: liveRun.run?.status ?? null,
            mode: liveRun.run?.mode ?? null,
            provider: liveRun.run?.provider ?? null,
            sections: liveRun.sections,
            events: liveRun.events,
            error: liveRun.error,
          }}
          onOpenInSiteBuilder={async () => {
            if (!id) return
            // Build-mode handoff — ensure the project has a sq_sites
            // row, then persist the current live run into real
            // sq_site_blocks when the destination page is empty.
            // No placeholder blocks in live mode.
            try {
              const { site, created } = await api.site.create({
                projectId: id,
                projectName: project?.name || 'Project',
              })
              if (site) {
                const { pages } = await api.site.pages(site.id)
                const home = pages.find((p) => p.is_homepage) ?? pages[0]
                if (home) {
                  const { blocks } = await api.site.blocks(home.id)
                  if (blocks.length === 0) {
                    // Prefer the live run's persisted sections when
                    // available — this turns Studio output into real
                    // sq_site_blocks instead of synthetic starter copy.
                    const liveSections = liveRun.sections ?? []
                    if (liveSections.length > 0) {
                      let order = 0
                      for (const section of liveSections) {
                        const mapped = mapSectionToBlock(section, order)
                        await api.site.createBlock({
                          pageId: home.id,
                          blockType: mapped.blockType,
                          contentJson: mapped.contentJson,
                          insertAt: order,
                        })
                        order += 1
                      }
                    }
                  }
                }
              }
              recordShellAction('studio.build_handoff', {
                projectId: id,
                siteId: site?.id,
                created,
                persistedSections: liveRun.sections.length,
              })
            } catch (err) {
              recordShellAction('studio.build_handoff_failed', {
                projectId: id,
                message: (err as Error)?.message ?? 'unknown',
              })
            }
            navigate(`/square/projects/${id}/site`)
          }}
        />
      </div>

      {/* Recent runs (live) */}
      {loading ? (
        <EmptyState title="Loading studio runs" body="Reading sq_workspace_runs." />
      ) : error ? (
        <EmptyState title="Studio unavailable" body={error} />
      ) : runs.length === 0 ? (
        <EmptyState
          title="No project runs yet"
          body="This project has no Content Studio run history in sq_workspace_runs yet."
        />
      ) : (
        <>
          <div
            style={{
              ...text.kicker,
              fontSize: 9,
              letterSpacing: '0.24em',
              color: SHELL_TOKENS.inkWhisper,
              marginBottom: 10,
            }}
          >
            RECENT RUNS
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 22 }}>
            {runs.slice(0, 6).map((run) => (
              <div
                key={run.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 16,
                  padding: '14px 16px',
                  borderRadius: 10,
                  background: SHELL_TOKENS.surface,
                  border: `1px solid ${SHELL_TOKENS.border}`,
                }}
              >
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: SHELL_TOKENS.ink }}>
                    {run.mode}
                    {run.mode === 'presentation' ? ' · Gamma-powered' : ''}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: SHELL_TOKENS.inkMuted,
                      fontFamily: 'var(--font-mono)',
                      marginTop: 4,
                    }}
                  >
                    {run.provider} · progress {run.progress}% · {run.status}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: SHELL_TOKENS.inkDim,
                      marginTop: 8,
                      lineHeight: 1.5,
                    }}
                  >
                    {run.prompt}
                  </div>
                </div>

                <div
                  style={{
                    fontSize: 10,
                    color: SHELL_TOKENS.inkWhisper,
                    fontFamily: 'var(--font-mono)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {timeAgo(run.created_at)}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <div
        style={{
          ...text.kicker,
          fontSize: 9,
          letterSpacing: '0.24em',
          color: SHELL_TOKENS.inkWhisper,
          marginBottom: 10,
        }}
      >
        AGENT ACTIVITY
      </div>
      {eventsLoading ? (
        <EmptyState title="Loading agent activity" body="Reading sq_workspace_agent_events." />
      ) : eventsError ? (
        <EmptyState title="Agent activity unavailable" body={eventsError} />
      ) : events.length === 0 ? (
        <EmptyState
          title="No agent activity yet"
          body="Agent events will appear here as sq_workspace_agent_events rows arrive."
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {events.map((event) => (
            <div
              key={event.id}
              style={{
                padding: '12px 14px',
                borderRadius: 10,
                background: SHELL_TOKENS.surface,
                border: `1px solid ${SHELL_TOKENS.border}`,
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  color: SHELL_TOKENS.inkWhisper,
                  fontFamily: 'var(--font-mono)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: 6,
                }}
              >
                {event.agent_name} · {event.event_type} · {timeAgo(event.created_at)}
              </div>
              <div style={{ fontSize: 13, color: SHELL_TOKENS.ink }}>
                {event.message}
              </div>
            </div>
          ))}
        </div>
      )}
    </ProjectSurface>
  )
}
